import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { FileText, BookX, Calendar } from 'lucide-react';
import { apiClient } from '../../utils/apiHelper';
import { formatDate } from '../../utils/dateTimeUtils';

const LibraryDamageReport = () => {
  const [damages, setDamages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [damageTypeFilter, setDamageTypeFilter] = useState('all');

  useEffect(() => { fetchDamages(); }, []);

  const fetchDamages = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/api/library/damaged-books');
      setDamages(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error:', error);
      setDamages([]);
    } finally {
      setLoading(false);
    }
  };

  // Get unique damage types
  const damageTypes = [...new Set(damages.map(d => d.damage_type).filter(Boolean))];

  // Filter data
  const getFilteredData = () => {
    let filtered = [...damages];
    
    if (statusFilter && statusFilter !== 'all') {
      if (statusFilter === 'Paid') {
        filtered = filtered.filter(d => d.penalty_paid === true || d.payment_status === 'Paid');
      } else {
        filtered = filtered.filter(d => d.penalty_paid !== true && d.payment_status !== 'Paid');
      }
    }
    
    if (damageTypeFilter && damageTypeFilter !== 'all') {
      filtered = filtered.filter(d => d.damage_type === damageTypeFilter);
    }
    
    if (startDate) {
      filtered = filtered.filter(d => d.damage_date >= startDate);
    }
    if (endDate) {
      filtered = filtered.filter(d => d.damage_date <= endDate);
    }
    
    return filtered;
  };

  const filteredDamages = getFilteredData();
  
  // Calculate summary - using penalty_amount from DB
  const totalDamageValue = filteredDamages.reduce((sum, d) => sum + (d.penalty_amount || d.fine_amount || d.damage_amount || 0), 0);
  const paidDamages = filteredDamages.filter(d => d.penalty_paid === true || d.payment_status === 'Paid');
  const collected = paidDamages.reduce((sum, d) => sum + (d.penalty_amount || d.fine_amount || d.damage_amount || 0), 0);
  const pending = totalDamageValue - collected;

  // Group by damage type
  const damageByType = {};
  filteredDamages.forEach(d => {
    const type = d.damage_type || 'Other';
    if (!damageByType[type]) {
      damageByType[type] = { count: 0, total: 0, paid: 0 };
    }
    damageByType[type].count++;
    damageByType[type].total += (d.penalty_amount || d.fine_amount || d.damage_amount || 0);
    if (d.penalty_paid === true || d.payment_status === 'Paid') {
      damageByType[type].paid += (d.penalty_amount || d.fine_amount || d.damage_amount || 0);
    }
  });

  const getDamageTypeBadge = (type) => {
    const colors = {
      'Lost': 'bg-red-100 text-red-800',
      'Torn': 'bg-orange-100 text-orange-800',
      'Water Damage': 'bg-blue-100 text-blue-800',
      'Fire Damage': 'bg-yellow-100 text-yellow-800',
      'Defaced': 'bg-purple-100 text-purple-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="p-6 space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <BookX className="w-5 h-5 text-red-600" />
            Library Damage Report
          </CardTitle>
          <div className="flex gap-2 items-center flex-wrap">
            <Select value={damageTypeFilter} onValueChange={setDamageTypeFilter}>
              <SelectTrigger className="w-36">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {damageTypes.map(type => (
                  <SelectItem key={type} value={type}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="Paid">Paid</SelectItem>
                <SelectItem value="Unpaid">Unpaid</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-500" />
              <Input 
                type="date" 
                value={startDate} 
                onChange={(e) => setStartDate(e.target.value)} 
                className="w-36"
              />
              <span className="text-gray-500">to</span>
              <Input 
                type="date" 
                value={endDate} 
                onChange={(e) => setEndDate(e.target.value)} 
                className="w-36"
              />
            </div>
            <Button onClick={fetchDamages} disabled={loading}>
              <FileText className="w-4 h-4 mr-2" />{loading ? 'Loading...' : 'Generate'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <p className="text-sm text-red-600">Total Damaged Items</p>
              <p className="text-2xl font-bold text-red-700">{filteredDamages.length}</p>
            </div>
            <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
              <p className="text-sm text-orange-600">Total Fine Amount</p>
              <p className="text-2xl font-bold text-orange-700">₹{totalDamageValue.toLocaleString('en-IN')}</p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <p className="text-sm text-green-600">Collected</p>
              <p className="text-2xl font-bold text-green-700">₹{collected.toLocaleString('en-IN')}</p>
            </div>
            <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
              <p className="text-sm text-yellow-600">Pending</p>
              <p className="text-2xl font-bold text-yellow-700">₹{pending.toLocaleString('en-IN')}</p>
            </div>
          </div>

          <div className="text-sm text-gray-500 mb-2">
            Showing {filteredDamages.length} damage records
          </div>

          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="text-left p-3">Book Title</th>
                <th className="text-left p-3">ISBN/Accession</th>
                <th className="text-left p-3">Member</th>
                <th className="text-left p-3">Damage Type</th>
                <th className="text-left p-3">Date</th>
                <th className="text-right p-3">Fine Amount</th>
                <th className="text-center p-3">Payment Mode</th>
                <th className="text-center p-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredDamages.length === 0 ? (
                <tr><td colSpan="8" className="text-center p-8 text-gray-500">No library damage records found.</td></tr>
              ) : (
                filteredDamages.map((damage, idx) => (
                  <tr key={idx} className="border-b hover:bg-gray-50">
                    <td className="p-3 font-medium">{damage.book_title || '-'}</td>
                    <td className="p-3 text-sm text-gray-600">{damage.isbn || damage.accession_number || '-'}</td>
                    <td className="p-3">{damage.member_name || damage.student_name || '-'}</td>
                    <td className="p-3">
                      <span className={`px-2 py-1 text-xs rounded-full ${getDamageTypeBadge(damage.damage_type)}`}>
                        {damage.damage_type || 'Unknown'}
                      </span>
                    </td>
                    <td className="p-3">{damage.damage_date ? formatDate(damage.damage_date) : '-'}</td>
                    <td className="p-3 text-right text-red-600 font-semibold">
                      ₹{(damage.penalty_amount || damage.fine_amount || damage.damage_amount || 0).toLocaleString('en-IN')}
                    </td>
                    <td className="p-3 text-center text-sm text-gray-600">
                      {damage.payment_mode || '-'}
                    </td>
                    <td className="p-3 text-center">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        damage.penalty_paid === true || damage.payment_status === 'Paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {damage.penalty_paid === true || damage.payment_status === 'Paid' ? 'Paid' : 'Unpaid'}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Summary by Damage Type */}
          {Object.keys(damageByType).length > 0 && (
            <div className="mt-6 border-t pt-4">
              <h3 className="font-semibold mb-3">Summary by Damage Type</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                {Object.entries(damageByType).map(([type, data]) => (
                  <div key={type} className="p-3 bg-gray-50 rounded-lg text-sm">
                    <p className="font-medium flex items-center gap-2">
                      <span className={`px-2 py-0.5 text-xs rounded-full ${getDamageTypeBadge(type)}`}>{type}</span>
                    </p>
                    <p className="text-gray-600 mt-1">Count: {data.count}</p>
                    <p className="text-red-600">Total: ₹{data.total.toLocaleString('en-IN')}</p>
                    <p className="text-green-600">Paid: ₹{data.paid.toLocaleString('en-IN')}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default LibraryDamageReport;
